import React, { useEffect, useRef, useState, useCallback } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { MousePointerClick, Ruler, Crosshair, Upload, Save, Loader2, ChevronLeft, ChevronRight, Pencil, Trash2, Info, Sparkles, Wand2, Plus, Minus, ZoomIn, Hand, Square, LayoutGrid, ScanLine, Copy, Layers, Bookmark, Camera, Calculator } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { track } from "@/lib/analytics";
import AskDrawingChatbot from "@/components/takeoff/AskDrawingChatbot";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

const TARGET_W = 880;
const MAX_RENDER_W = 2600;

// Downscale + JPEG-compress a data URL before sending to the AI vision model.
// Big blueprint images (esp. full-res photo/PNG uploads) make Gemini calls slow;
// ~1600px JPEG keeps schedules/legends legible while cutting payload dramatically.
async function toAIImage(dataUrl, maxW = 1600, quality = 0.82) {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxW / (img.naturalWidth || maxW));
        const w = Math.max(1, Math.round((img.naturalWidth || maxW) * scale));
        const h = Math.max(1, Math.round((img.naturalHeight || maxW) * scale));
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        const cx = c.getContext("2d");
        cx.fillStyle = "#ffffff"; cx.fillRect(0, 0, w, h);
        cx.drawImage(img, 0, 0, w, h);
        resolve(c.toDataURL("image/jpeg", quality));
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    } catch { resolve(dataUrl); }
  });
}
const UNITS = ["m", "cm", "mm", "ft", "in"];
// Distinct marker colours assigned per named symbol type (Count By Colour).
const SYMBOL_PALETTE = ["#059669", "#2563EB", "#D97706", "#7C3AED", "#DB2777", "#0891B2", "#EA580C", "#65A30D"];
// Trade-aware wall/area presets: picking a material auto-fills a sensible height (m).
const AREA_MATERIALS = [
  { label: "Drylining / Stud wall", height: "2.4" },
  { label: "Blockwork (internal)", height: "2.4" },
  { label: "Facing bricks (external)", height: "2.7" },
  { label: "Trench block / concrete fill", height: "1.0" },
  { label: "Full-height partition", height: "3.0" },
];
const HEIGHT_CHIPS = ["2.4", "2.5", "2.7", "1.0"];
const dist = (a, b) => Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);

const TakeoffCanvas = ({ projectId }) => {
  const { user } = useAuth();
  const [isPhone, setIsPhone] = useState(typeof window !== "undefined" && window.innerWidth < 768);
  const [isTouch] = useState(typeof window !== "undefined" && window.matchMedia && window.matchMedia("(pointer: coarse)").matches);
  const [zoom, setZoom] = useState(1);
  const pinchRef = useRef(null);
  const scrollRef = useRef(null);
  const [showPanHint, setShowPanHint] = useState(false);
  const clampZoom = (z) => Math.min(4, Math.max(0.5, Math.round(z * 100) / 100));
  // Fit the drawing to the visible canvas width on load so it opens large and
  // readable (was opening small at a fixed width, which looked blurry). Capped
  // so it never exceeds the rendered raster resolution.
  const fitToWidth = useCallback((logicalW) => {
    const cw = (scrollRef.current?.clientWidth || logicalW) - 32;
    setZoom(Math.max(1, Math.min(2.5, Math.round((cw / logicalW) * 100) / 100)));
  }, []);
  useEffect(() => {
    const onResize = () => setIsPhone(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const [bg, setBg] = useState(null);
  const [dims, setDims] = useState({ w: TARGET_W, h: 560 });
  const [hasBlueprint, setHasBlueprint] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rendering, setRendering] = useState(false);
  const [tool, setTool] = useState("count");
  const [annotations, setAnnotations] = useState([]);
  const [scaleMap, setScaleMap] = useState({});
  const [pending, setPending] = useState(null);
  const [saving, setSaving] = useState(false);
  const [numPages, setNumPages] = useState(1);
  const [pageNum, setPageNum] = useState(1);
  const [menu, setMenu] = useState(null);            // { x, y, id } line context menu
  const [editLine, setEditLine] = useState(null);    // { id, value }
  const [calib, setCalib] = useState(null);          // { distance, value, unit }
  const [areaPrompt, setAreaPrompt] = useState(null); // { x1,y1,x2,y2,px,length,unit,height,material }
  const [lastAreaHeight, setLastAreaHeight] = useState("2.4"); // session memory for wall height
  const [wallTypes, setWallTypes] = useState([]);
  const [wallTypeMgr, setWallTypeMgr] = useState(false);
  const [newWT, setNewWT] = useState({ label: "", height: "2.4", rate: "" });
  const [presets, setPresets] = useState([]);
  const [aiBusy, setAiBusy] = useState(null);        // 'extract' | 'scale' | null
  const [extractResult, setExtractResult] = useState(null); // { items:[{...,include}] }
  const [isPdf, setIsPdf] = useState(false);
  const [detectResult, setDetectResult] = useState(null); // { scale_ratio, units_per_px, dims:[{...,include}] }
  const [detectFilter, setDetectFilter] = useState({ min: 0, max: 0 }); // px line-length filter in detect dialog
  const [symbolResult, setSymbolResult] = useState(null); // { matches, count, box, name, threshold, preset, color, save }
  const [recounting, setRecounting] = useState(false);
  const [symbolPresets, setSymbolPresets] = useState([]);
  const [detectAllResult, setDetectAllResult] = useState(null); // { groups:[{id,count,color,matches,template_base64,name,include}] }
  const [dragStart, setDragStart] = useState(null);
  const [dragBox, setDragBox] = useState(null);           // { x,y,w,h } symbol selection rect
  const fileRef = useRef(null);
  const photoRef = useRef(null);
  const [pricing, setPricing] = useState(false);
  const [snapping, setSnapping] = useState(false);
  const wrapRef = useRef(null);
  const pdfRef = useRef(null);
  const imgUrlRef = useRef(null);

  const renderPage = useCallback(async (n) => {
    setRendering(true);
    try {
      if (pdfRef.current) {
        const page = await pdfRef.current.getPage(n);
        const base = page.getViewport({ scale: 1 });
        const logicalScale = TARGET_W / base.width;
        // Rasterise at higher resolution than we display so drawings stay crisp
        // at 100% and when zoomed in (a low-res raster looked blurry - bug fix).
        const renderScale = Math.min(TARGET_W * 3, MAX_RENDER_W) / base.width;
        const viewport = page.getViewport({ scale: renderScale });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext("2d");
        await page.render({ canvasContext: ctx, viewport }).promise;
        setBg(canvas.toDataURL("image/png"));
        setDims({ w: base.width * logicalScale, h: base.height * logicalScale });
      }
    } catch { toast.error("Could not render page"); }
    finally { setRendering(false); }
  }, []);

  // Render a PDF page to a downscaled base64 image for AI (does not touch the view).
  const renderPageDataUrl = useCallback(async (n) => {
    if (!pdfRef.current) return null;
    const page = await pdfRef.current.getPage(n);
    const base = page.getViewport({ scale: 1 });
    const scale = Math.min(1500, base.width * 2) / base.width;
    const vp = page.getViewport({ scale });
    const c = document.createElement("canvas");
    c.width = vp.width; c.height = vp.height;
    await page.render({ canvasContext: c.getContext("2d"), viewport: vp }).promise;
    return await toAIImage(c.toDataURL("image/png"));
  }, []);

  // All pages of the drawing set (capped) for the "Ask the drawing" chatbot.
  const getAllImages = useCallback(async () => {
    if (pdfRef.current) {
      const n = Math.min(numPages, 8);
      const out = [];
      for (let i = 1; i <= n; i++) { const d = await renderPageDataUrl(i); if (d) out.push(d); }
      return out;
    }
    return bg ? [await toAIImage(bg)] : [];
  }, [numPages, renderPageDataUrl, bg]);


  const renderBlueprint = useCallback(async (bp) => {
    setRendering(true);
    try {
      const resp = await api.get(`/files/${bp.storage_path}`, { responseType: "arraybuffer" });
      if (bp.content_type === "application/pdf") {
        const pdf = await pdfjsLib.getDocument({ data: resp.data }).promise;
        pdfRef.current = pdf;
        setIsPdf(true);
        setNumPages(pdf.numPages);
        setPageNum(1);
        await renderPage(1);
        fitToWidth(TARGET_W);
      } else {
        pdfRef.current = null;
        setIsPdf(false);
        setNumPages(1);
        setPageNum(1);
        const blob = new Blob([resp.data], { type: bp.content_type });
        const url = URL.createObjectURL(blob);
        imgUrlRef.current = url;
        const img = new Image();
        img.onload = () => {
          const w = Math.min(TARGET_W, img.naturalWidth);
          const h = (img.naturalHeight / img.naturalWidth) * w;
          setDims({ w, h });
          fitToWidth(w);
          // Render the image to a canvas and use a base64 data URL so the AI
          // endpoints (which expect a base64 image) work for PNG/JPEG uploads
          // too (blob: URLs can't be base64-decoded by Gemini).
          try {
            const canvas = document.createElement("canvas");
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);
            setBg(canvas.toDataURL("image/png"));
          } catch {
            setBg(url);
          }
          setRendering(false);
        };
        img.src = url;
      }
      setHasBlueprint(true);
    } catch {
      toast.error("Could not render blueprint");
      setRendering(false);
    }
  }, [renderPage, fitToWidth]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const t = await api.get(`/projects/${projectId}/takeoff`);
      const anns = (t.data.annotations || []).map((a) => ({ ...a, page: a.page || 1, id: a.id || crypto.randomUUID() }));
      setAnnotations(anns);
      const sc = t.data.scale;
      if (sc && sc.units_per_px) setScaleMap({ 1: sc });
      else setScaleMap(sc || {});
    } catch (e) {
      console.debug("No saved takeoff yet:", e?.response?.status);
    }
    try {
      const bp = await api.get(`/projects/${projectId}/blueprint`);
      await renderBlueprint(bp.data);
    } catch { setHasBlueprint(false); }
    finally { setLoading(false); }
  }, [projectId, renderBlueprint]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { api.get("/description-presets").then(({ data }) => setPresets(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/symbol-presets").then(({ data }) => setSymbolPresets(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/wall-types").then(({ data }) => setWallTypes(data)).catch(() => {}); }, []);

  useEffect(() => { if (hasBlueprint) window.dispatchEvent(new Event("tradeoff:takeoff-ready")); }, [hasBlueprint]);

  // Snap an AI-extracted description to a saved preset (canonical name + unit) on a strong match, for consistent naming.
  const normalizeToPreset = useCallback((desc) => {
    if (!presets.length || !desc) return null;
    const d = desc.toLowerCase().trim();
    let best = null, bestScore = 0;
    for (const p of presets) {
      const pd = p.description.toLowerCase();
      let score = 0;
      if (d === pd) score = 3;
      else if (d.includes(pd) || pd.includes(d)) score = 2;
      if (score > bestScore) { bestScore = score; best = p; }
    }
    return bestScore >= 2 ? best : null;
  }, [presets]);

  // close context menu on any outside click / escape
  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    return () => { window.removeEventListener("click", close); window.removeEventListener("scroll", close, true); };
  }, [menu]);

  const goPage = async (n) => {
    if (n < 1 || n > numPages || n === pageNum) return;
    setPending(null);
    setPageNum(n);
    if (pdfRef.current) await renderPage(n);
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setRendering(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/blueprint`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAnnotations([]); setScaleMap({});
      await renderBlueprint(data);
      toast.success("Blueprint uploaded");
    } catch { toast.error("Upload failed"); setRendering(false); }
  };

  const point = (e) => {
    const rect = wrapRef.current.getBoundingClientRect();
    const sx = rect.width / dims.w || 1;
    const sy = rect.height / dims.h || 1;
    return { x: Math.round((e.clientX - rect.left) / sx), y: Math.round((e.clientY - rect.top) / sy) };
  };

  const touchDist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  const touchMid = (t) => ({ x: (t[0].clientX + t[1].clientX) / 2, y: (t[0].clientY + t[1].clientY) / 2 });
  const onTouchStart = (e) => {
    if (e.touches.length === 2) pinchRef.current = { d0: touchDist(e.touches), z0: zoom, mid: touchMid(e.touches) };
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinchRef.current) {
      const d = touchDist(e.touches);
      setZoom(clampZoom(pinchRef.current.z0 * (d / pinchRef.current.d0)));
      // Two-finger drag-to-pan: shift the scroll container by the centroid movement
      const mid = touchMid(e.touches);
      const sc = scrollRef.current;
      if (sc) {
        sc.scrollLeft -= mid.x - pinchRef.current.mid.x;
        sc.scrollTop -= mid.y - pinchRef.current.mid.y;
      }
      pinchRef.current.mid = mid;
    }
  };
  const onTouchEnd = () => { pinchRef.current = null; };

  useEffect(() => {
    if (isTouch && !isPhone && hasBlueprint && !localStorage.getItem("bs_pan_hint_seen")) {
      setShowPanHint(true);
      localStorage.setItem("bs_pan_hint_seen", "1");
      const t = setTimeout(() => setShowPanHint(false), 6000);
      return () => clearTimeout(t);
    }
  }, [isTouch, isPhone, hasBlueprint]);

  const pageScale = scaleMap[pageNum];

  const handleClick = (e) => {
    if (!hasBlueprint) return;
    const p = point(e);
    if (tool === "count") {
      setAnnotations((a) => [...a, { id: crypto.randomUUID(), type: "count", page: pageNum, x: p.x, y: p.y }]);
    } else if (tool === "line" || tool === "calibrate" || tool === "area") {
      if (!pending) { setPending(p); return; }
      const d = dist(pending, p);
      if (d < 3) { setPending(null); return; }
      if (tool === "calibrate") {
        setCalib({ distance: d, value: "", unit: pageScale?.unit || "m" });
        setPending(null);
      } else if (tool === "area") {
        setAreaPrompt({
          x1: pending.x, y1: pending.y, x2: p.x, y2: p.y, px: d,
          length: pageScale ? d * pageScale.units_per_px : d,
          unit: pageScale?.unit || "px", height: lastAreaHeight || "2.4", material: "",
        });
        setPending(null);
      } else {
        const length = pageScale ? d * pageScale.units_per_px : d;
        setAnnotations((a) => [...a, { id: crypto.randomUUID(), type: "line", page: pageNum, x1: pending.x, y1: pending.y, x2: p.x, y2: p.y, px: d, length }]);
        window.dispatchEvent(new Event("tradeoff:line-added"));
        setPending(null);
      }
    }
  };

  const confirmArea = (withHeight) => {
    const base = {
      id: crypto.randomUUID(), page: pageNum,
      x1: areaPrompt.x1, y1: areaPrompt.y1, x2: areaPrompt.x2, y2: areaPrompt.y2,
      px: areaPrompt.px, length: areaPrompt.length, timestamp: new Date().toISOString(),
    };
    if (withHeight) {
      const h = parseFloat(areaPrompt.height);
      if (!h || h <= 0) { toast.error("Enter a valid height greater than 0"); return; }
      const area = areaPrompt.length * h;
      const upp = pageScale?.units_per_px;
      const heightPx = upp ? h / upp : h;
      setLastAreaHeight(String(h));
      setAnnotations((a) => [...a, { ...base, type: "area", height: h, area, heightPx, distanceMeters: areaPrompt.length, heightMeters: h, totalM2: area, material: areaPrompt.material || "" }]);
      toast.success(`Area added: ${area.toFixed(2)} ${areaPrompt.unit}²`);
    } else {
      setAnnotations((a) => [...a, { ...base, type: "line" }]);
    }
    setAreaPrompt(null);
  };

  const addWallType = async () => {
    const label = newWT.label.trim();
    if (!label) { toast.error("Enter a wall type name"); return; }
    try {
      const { data } = await api.post("/wall-types", { label, height: Number(newWT.height) || 2.4, rate: Number(newWT.rate) || 0, unit: "m2" });
      setWallTypes((w) => [...w.filter((x) => x.label !== data.label), data].sort((a, b) => a.label.localeCompare(b.label)));
      setNewWT({ label: "", height: "2.4", rate: "" });
      toast.success(`Saved wall type “${label}”.`);
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't save wall type"); }
  };

  const deleteWallType = async (wt) => {
    setWallTypes((w) => w.filter((x) => x.id !== wt.id));
    try { await api.delete(`/wall-types/${wt.id}`); } catch { /* ignore */ }
  };

  const addAreasToEstimate = async () => {
    const areas = annotations.filter((a) => a.type === "area" && a.area);
    if (!areas.length) { toast.info("Draw a Wall / Area measurement with a height first"); return; }
    try {
      const [{ data: est }, { data: rates }] = await Promise.all([
        api.get(`/projects/${projectId}/estimate`),
        api.get(`/rates`).catch(() => ({ data: [] })),
      ]);
      const existing = est.line_items || [];
      const findRate = (name) => {
        const n = (name || "").trim().toLowerCase();
        if (!n) return null;
        return (rates || []).find((r) => (r.description || "").trim().toLowerCase() === n)
          || (rates || []).find((r) => (r.description || "").toLowerCase().includes(n));
      };
      let priced = 0;
      const additions = areas.map((a) => {
        const wt = (wallTypes || []).find((w) => (w.label || "").trim().toLowerCase() === (a.material || "").trim().toLowerCase());
        let rate = 0;
        if (wt && Number(wt.rate) > 0) rate = Number(wt.rate);
        else { const rc = a.material ? findRate(a.material) : null; if (rc) rate = Number(rc.rate) || 0; }
        if (rate > 0) priced += 1;
        return {
          id: (crypto.randomUUID ? crypto.randomUUID() : String(Math.random())),
          description: a.material || "Wall area (from takeoff)",
          qty: Number((a.area || 0).toFixed(2)), unit: "m2",
          rate, markup: 0,
        };
      });
      await api.put(`/projects/${projectId}/estimate`, { line_items: [...existing, ...additions] });
      toast.success(`Added ${additions.length} area line${additions.length === 1 ? "" : "s"} to the estimate${priced ? ` · ${priced} priced from your rate card` : ""}`);
    } catch { toast.error("Couldn't add to estimate"); }
  };

  // Push a single AI-answered quantity (from the Ask-the-drawing chatbot) into the estimate.
  const pushChatItemToEstimate = async (item) => {
    const { data: est } = await api.get(`/projects/${projectId}/estimate`);
    const existing = est.line_items || [];
    const line = {
      id: (crypto.randomUUID ? crypto.randomUUID() : String(Math.random())),
      description: item.description || "From drawing (AI)",
      qty: Number(item.quantity) || 0,
      unit: item.unit || "no",
      rate: 0, markup: 0,
    };
    await api.put(`/projects/${projectId}/estimate`, { line_items: [...existing, line] });
    window.dispatchEvent(new Event("tradeoff:estimate-line-added"));
  };

  const confirmCalibration = () => {    const val = parseFloat(calib.value);
    if (!val || val <= 0) { toast.error("Enter a valid length greater than 0"); return; }
    const upp = val / calib.distance;
    setScaleMap((m) => ({ ...m, [pageNum]: { units_per_px: upp, unit: calib.unit } }));
    // recompute existing line lengths on this page from stored pixel distance
    setAnnotations((a) => a.map((x) => (x.type === "line" && (x.page || 1) === pageNum && x.px)
      ? { ...x, length: x.px * upp } : x));
    setCalib(null);
    window.dispatchEvent(new Event("tradeoff:calibrated"));
    toast.success(`Scale set for page ${pageNum}. Measurements now shown in ${calib.unit}.`);
  };

  const openMenu = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    const x = Math.min(e.clientX, window.innerWidth - 168);
    const y = Math.min(e.clientY, window.innerHeight - 100);
    setMenu({ x: Math.max(8, x), y: Math.max(8, y), id });
  };

  const startEdit = () => {
    const line = annotations.find((a) => a.id === menu.id);
    setEditLine({ id: menu.id, value: String((line?.length ?? 0).toFixed(2)) });
    setMenu(null);
  };

  const confirmEdit = () => {
    const val = parseFloat(editLine.value);
    if (isNaN(val) || val < 0) { toast.error("Enter a valid length"); return; }
    setAnnotations((a) => a.map((x) => (x.id === editLine.id ? { ...x, length: val } : x)));
    setEditLine(null);
  };

  const deleteFromMenu = () => { removeWithUndo(menu.id, "Line"); setMenu(null); };
  const removeWithUndo = (id, label = "Item") => {
    const removed = annotations.find((x) => x.id === id);
    setAnnotations((a) => a.filter((x) => x.id !== id));
    if (removed) toast.success(`${label} removed`, { action: { label: "Undo", onClick: () => setAnnotations((a) => [...a, removed]) } });
  };

  const celebrateFirstTakeoff = () => {
    if (annotations.length < 1) return;
    const key = `bs_first_takeoff:${user?.tenant_id || "anon"}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, "1");
    track("first_takeoff_completed", {});
    try {
      const burst = (opts) => confetti({ particleCount: 90, spread: 70, startVelocity: 45, origin: { y: 0.7 }, colors: ["#2563EB", "#16305C", "#22c55e", "#ffffff"], ...opts });
      burst({ angle: 60, origin: { x: 0, y: 0.75 } });
      burst({ angle: 120, origin: { x: 1, y: 0.75 } });
      setTimeout(() => burst({ origin: { x: 0.5, y: 0.6 } }), 200);
    } catch (e) { /* confetti is best-effort */ }
    toast.success("🎉 Your first takeoff — nice one! You're on the tools now. Build an estimate next.", { duration: 6000 });
  };

  const save = async () => {
    setSaving(true);
    try { await api.put(`/projects/${projectId}/takeoff`, { annotations, scale: scaleMap }); toast.success("Takeoff saved"); celebrateFirstTakeoff(); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const aiExtract = async () => {
    if (!bg) { toast.error("Open a blueprint page first"); return; }
    setAiBusy("extract");
    try {
      const image = await toAIImage(bg);
      const { data } = await api.post(`/projects/${projectId}/ai/extract-quantities`, { image_base64: image });
      if (!data.items?.length) { toast.info("No schedule or legend found on this page"); return; }
      setExtractResult({ items: data.items.map((it) => ({ ...it, include: true })) });
    } catch (err) { toast.error(err.response?.data?.detail || "AI extraction failed"); }
    finally { setAiBusy(null); }
  };

  const runAiTakeoff = async () => {
    if (!bg) { toast.error("Open a blueprint page first"); return; }
    setAiBusy("run");
    try {
      const image = await toAIImage(bg);
      const { data } = await api.post(`/projects/${projectId}/ai/run-takeoff`, { image_base64: image });
      if (data.scale) {
        const pct = Math.round((data.confidence || 0) * 100);
        toast.success(`AI first pass done · detected scale ${data.scale} (${pct}% confident)`, {
          description: "Use Calibrate to lock the scale in by clicking two points on a known dimension.",
          duration: 9000,
        });
      }
      const items = data.items || [];
      if (items.length) {
        setExtractResult({ items: items.map((it) => ({ ...it, include: true })), scale: data.scale });
      } else {
        toast.info(data.scale
          ? "Scale detected above — no schedule or legend quantities were found on this page."
          : "AI couldn't find a schedule, legend or scale on this page. Try a clearer page, or calibrate and take off manually.");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "AI takeoff failed"); }
    finally { setAiBusy(null); }
  };

  const applyExtract = async (withPricing = false) => {
    const chosen = extractResult.items.filter((i) => i.include);
    if (!chosen.length) { setExtractResult(null); return; }
    if (withPricing) setPricing(true);
    try {
      const { data: est } = await api.get(`/projects/${projectId}/estimate`);
      const existing = est.line_items || [];
      const additions = chosen.map((i) => {
        const p = normalizeToPreset(i.description);
        return {
          id: (crypto.randomUUID ? crypto.randomUUID() : String(Math.random())),
          description: p ? p.description : i.description, qty: i.quantity, unit: p ? p.unit : i.unit, rate: 0, markup: 0,
        };
      });
      const aligned = chosen.filter((i) => normalizeToPreset(i.description)).length;
      let merged = [...existing, ...additions];
      await api.put(`/projects/${projectId}/estimate`, { line_items: merged });
      let priced = 0;
      let emptyLibrary = false;
      if (withPricing) {
        try {
          const { data: sugg } = await api.post(`/projects/${projectId}/ai/suggest-rates`);
          const byIndex = {};
          (sugg.suggestions || []).forEach((s) => { if (s.suggested_rate != null) byIndex[s.index] = s.suggested_rate; });
          merged = merged.map((l, idx) => (byIndex[idx] != null && (!l.rate || l.rate === 0)) ? (priced++, { ...l, rate: byIndex[idx] }) : l);
          if (priced) await api.put(`/projects/${projectId}/estimate`, { line_items: merged });
        } catch (e) {
          const msg = e.response?.data?.detail;
          if (typeof msg === "string" && msg.toLowerCase().includes("rate library")) emptyLibrary = true;
        }
      }
      const parts = [`Added ${additions.length} line${additions.length === 1 ? "" : "s"} to the estimate`];
      if (priced) parts.push(`priced ${priced} from your rate library`);
      if (aligned) parts.push(`${aligned} name${aligned === 1 ? "" : "s"} aligned to your presets`);
      toast.success(parts.join(" · "));
      if (emptyLibrary) toast.info("Add a few rates to your rate library so AI can price automatically next time.");
      setExtractResult(null);
    } catch { toast.error("Couldn't add to estimate"); }
    finally { setPricing(false); }
  };

  const onSnapPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setSnapping(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
      });
      const image = await toAIImage(dataUrl);
      const { data } = await api.post(`/projects/${projectId}/ai/run-takeoff`, { image_base64: image });
      if (data.scale) {
        const pct = Math.round((data.confidence || 0) * 100);
        toast.success(`Detected scale ${data.scale} (${pct}% confident)`);
      }
      const items = data.items || [];
      if (items.length) {
        setExtractResult({ items: items.map((it) => ({ ...it, include: true })) });
      } else {
        toast.info("Couldn't read a schedule or legend from that photo. Try a clear, straight-on shot of the schedule table.");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't read that photo"); }
    finally { setSnapping(false); }
  };

  const startDetectRegion = () => {
    if (!bg) { toast.error("Open a blueprint page first"); return; }
    setDragBox(null);
    setTool("detect-region");
    toast.info("Drag a box around the area to scan — e.g. one room — to skip borders, grids and title blocks.");
  };

  const detectMeasurements = async (region = null) => {
    setAiBusy("detect");
    try {
      const body = { page: pageNum, target_w: Math.round(dims.w) };
      const hasRegion = region && region.w > 4 && region.h > 4;
      if (hasRegion) body.region = [region.x, region.y, region.w, region.h];
      const { data } = await api.post(`/projects/${projectId}/ai/detect-measurements`, body);
      const dimsList = (data.detections || []).filter((d) => d.type === "dimension").map((d, i) => ({ ...d, include: true, _id: i }));
      if (!data.scale_ratio && dimsList.length === 0) {
        toast.info(data.notes || (data.page_type === "raster"
          ? "This scanned page had nothing we could read — use Detect scale or calibrate manually."
          : hasRegion ? "No dimensions found in that box — try a larger area or scan the whole page." : "No scale or dimensions found on this page."));
        return;
      }
      if (data.page_type === "raster") toast.info(data.notes || "Scanned drawing — double-check the values before applying.");
      const lens = dimsList.map((d) => d.px_len || 0);
      const maxPx = lens.length ? Math.ceil(Math.max(...lens)) : 0;
      setDetectFilter({ min: 0, max: maxPx });
      setDetectResult({ ...data, dims: dimsList, scanned_region: !!hasRegion });
    } catch (err) { toast.error(err.response?.data?.detail || "Detection failed"); }
    finally { setAiBusy(null); if (tool === "detect-region") setTool("line"); }
  };

  const detectDimVisible = (d) => (d.px_len || 0) >= (detectFilter.min || 0) && (!detectFilter.max || (d.px_len || 0) <= detectFilter.max);

  const applyDetection = () => {
    const r = detectResult;
    if (r.units_per_px) {
      setScaleMap((m) => ({ ...m, [pageNum]: { units_per_px: r.units_per_px, unit: r.unit || "m" } }));
      setAnnotations((a) => a.map((x) => (x.type === "line" && (x.page || 1) === pageNum && x.px) ? { ...x, length: x.px * r.units_per_px } : x));
    }
    const chosen = (r.dims || []).filter((d) => d.include && detectDimVisible(d));
    const additions = chosen.map((d) => {
      const [x1, y1, x2, y2] = d.line;
      const px = d.px_len || Math.hypot(x2 - x1, y2 - y1);
      return { id: crypto.randomUUID(), type: "line", page: pageNum, x1, y1, x2, y2, px, length: d.value_m != null ? d.value_m : (r.units_per_px ? px * r.units_per_px : px), source: r.page_type === "raster" ? "ocr" : undefined };
    });
    if (additions.length) setAnnotations((a) => [...a, ...additions]);
    if (r.units_per_px) window.dispatchEvent(new Event("tradeoff:calibrated"));
    if (additions.length) window.dispatchEvent(new Event("tradeoff:line-added"));
    setDetectResult(null);
    toast.success(`${r.units_per_px ? `Scale set (${r.scale_ratio}). ` : ""}${additions.length ? `Added ${additions.length} measured line${additions.length === 1 ? "" : "s"}.` : ""}`.trim() || "Applied");
  };

  const DEFAULT_SYMBOL_THRESHOLD = 0.72;
  const countSymbols = async (box) => {
    setAiBusy("symbol");
    try {
      const { data } = await api.post(`/projects/${projectId}/ai/count-symbols`, {
        page: pageNum, bbox: [box.x, box.y, box.w, box.h], img_w: dims.w, img_h: dims.h, threshold: DEFAULT_SYMBOL_THRESHOLD,
      });
      setDragBox(null);
      if (!data.count) { toast.info("No matching symbols found — try a tighter box, or lower the match strength."); }
      setSymbolResult({ ...data, box, name: "", threshold: DEFAULT_SYMBOL_THRESHOLD });
    } catch (err) { setDragBox(null); toast.error(err.response?.data?.detail || "Symbol count failed"); }
    finally { setAiBusy(null); }
  };

  const recountSymbols = async (threshold) => {
    if (!symbolResult) return;
    setRecounting(true);
    try {
      const body = symbolResult.box
        ? { page: pageNum, bbox: [symbolResult.box.x, symbolResult.box.y, symbolResult.box.w, symbolResult.box.h], img_w: dims.w, img_h: dims.h, threshold }
        : { page: pageNum, img_w: dims.w, img_h: dims.h, threshold, multiscale: true, template_base64: symbolResult.preset?.template_base64 };
      const { data } = await api.post(`/projects/${projectId}/ai/count-symbols`, body);
      setSymbolResult((r) => (r ? { ...r, matches: data.matches, count: data.count, threshold } : r));
    } catch { /* keep previous matches */ }
    finally { setRecounting(false); }
  };

  const symbolColorFor = (name) => {
    const existing = annotations.find((a) => a.type === "count" && a.symbol === name && a.color);
    if (existing) return existing.color;
    const used = new Set(annotations.filter((a) => a.type === "count" && a.color).map((a) => a.color));
    return SYMBOL_PALETTE.find((c) => !used.has(c)) || SYMBOL_PALETTE[used.size % SYMBOL_PALETTE.length];
  };

  // Crop the selected box out of the rendered blueprint as a PNG data URL (for saving a reusable symbol).
  const cropTemplate = (box) => new Promise((resolve) => {
    try {
      const img = new Image();
      img.onload = () => {
        const sx = (img.naturalWidth || dims.w) / dims.w, sy = (img.naturalHeight || dims.h) / dims.h;
        const c = document.createElement("canvas");
        c.width = Math.max(1, Math.round(box.w * sx)); c.height = Math.max(1, Math.round(box.h * sy));
        c.getContext("2d").drawImage(img, box.x * sx, box.y * sy, box.w * sx, box.h * sy, 0, 0, c.width, c.height);
        resolve(c.toDataURL("image/png"));
      };
      img.onerror = () => resolve(null);
      img.src = bg;
    } catch { resolve(null); }
  });

  const applySymbols = async () => {
    if (!symbolResult?.count) { setSymbolResult(null); return; }
    const name = (symbolResult.name || "").trim() || "Symbol";
    const color = symbolResult.color || symbolColorFor(name);
    const adds = symbolResult.matches.map((m) => ({ id: crypto.randomUUID(), type: "count", page: pageNum, x: Math.round(m.x), y: Math.round(m.y), auto: true, symbol: name, color }));
    setAnnotations((a) => [...a, ...adds]);
    if (symbolResult.save && symbolResult.box) {
      const tmpl = await cropTemplate(symbolResult.box);
      if (tmpl) {
        try {
          const { data } = await api.post("/symbol-presets", { name, template_base64: tmpl, color, rate: Number(symbolResult.saveRate) || 0, unit: "nr" });
          setSymbolPresets((p) => [...p.filter((x) => x.name !== data.name), data].sort((a, b) => a.name.localeCompare(b.name)));
        } catch { /* non-blocking */ }
      }
    }
    setSymbolResult(null);
    setTool("count");
    toast.success(`Added ${adds.length} “${name}” marker${adds.length === 1 ? "" : "s"}${symbolResult.save ? " · saved for reuse" : ""}.`);
  };

  const countFromPreset = async (preset) => {
    setAiBusy("symbol");
    try {
      const { data } = await api.post(`/projects/${projectId}/ai/count-symbols`, {
        page: pageNum, img_w: dims.w, img_h: dims.h, threshold: 0.62, multiscale: true, template_base64: preset.template_base64,
      });
      if (!data.count) { toast.info(`No “${preset.name}” symbols found on this page — try lowering the match strength.`); }
      setSymbolResult({ ...data, box: null, preset, name: preset.name, color: preset.color, threshold: 0.62, save: false });
    } catch (err) { toast.error(err.response?.data?.detail || "Symbol count failed"); }
    finally { setAiBusy(null); }
  };

  const deletePreset = async (preset) => {
    setSymbolPresets((p) => p.filter((x) => x.id !== preset.id));
    try { await api.delete(`/symbol-presets/${preset.id}`); } catch { /* ignore */ }
  };

  const detectAllSymbols = async () => {
    setAiBusy("detectall");
    try {
      const { data } = await api.post(`/projects/${projectId}/ai/detect-all-symbols`, { page: pageNum, img_w: dims.w, img_h: dims.h, min_count: 3 });
      if (!data.total_groups) { toast.info("No repeated symbols detected — try the manual Auto-count tool."); return; }
      setDetectAllResult({ groups: data.groups.map((g, i) => ({ ...g, name: `Symbol ${String.fromCharCode(65 + i)}`, include: true })) });
    } catch (err) { toast.error(err.response?.data?.detail || "Detection failed"); }
    finally { setAiBusy(null); }
  };

  const applyDetectAll = () => {
    const chosen = (detectAllResult?.groups || []).filter((g) => g.include);
    const adds = [];
    chosen.forEach((g) => {
      const name = (g.name || "").trim() || "Symbol";
      g.matches.forEach((m) => adds.push({ id: crypto.randomUUID(), type: "count", page: pageNum, x: Math.round(m.x), y: Math.round(m.y), auto: true, symbol: name, color: g.color }));
    });
    if (adds.length) setAnnotations((a) => [...a, ...adds]);
    setDetectAllResult(null);
    setTool("count");
    toast.success(`Added ${adds.length} marker${adds.length === 1 ? "" : "s"} across ${chosen.length} symbol type${chosen.length === 1 ? "" : "s"}.`);
  };

  const addCountsToEstimate = async () => {
    const groups = {};
    annotations.filter((a) => a.type === "count" && a.symbol).forEach((a) => { groups[a.symbol] = (groups[a.symbol] || 0) + 1; });
    const entries = Object.entries(groups);
    if (!entries.length) { toast.info("Auto-count and name some symbols first"); return; }
    try {
      const [{ data: est }, { data: rates }] = await Promise.all([
        api.get(`/projects/${projectId}/estimate`),
        api.get(`/rates`).catch(() => ({ data: [] })),
      ]);
      const existing = est.line_items || [];
      // Map each symbol name to a saved rate: exact (case-insensitive), else fuzzy contains.
      const findRate = (name) => {
        const n = name.trim().toLowerCase();
        return (rates || []).find((r) => (r.description || "").trim().toLowerCase() === n)
          || (rates || []).find((r) => n && (r.description || "").toLowerCase().includes(n));
      };
      const presetByName = (name) => symbolPresets.find((p) => (p.name || "").trim().toLowerCase() === name.trim().toLowerCase());
      let priced = 0;
      const additions = entries.map(([name, count]) => {
        const sp = presetByName(name);          // saved symbol may carry its own price
        const rateItem = findRate(name);         // else fall back to the rate card
        let rate = 0, unit = "nr", description = name;
        if (sp && Number(sp.rate) > 0) { rate = Number(sp.rate); unit = sp.unit || "nr"; }
        else if (rateItem) { rate = Number(rateItem.rate) || 0; unit = rateItem.unit || "nr"; description = rateItem.description; }
        else { const p = normalizeToPreset(name); unit = p ? p.unit : "nr"; description = p ? p.description : name; }
        if (rate > 0) priced += 1;
        return { id: (crypto.randomUUID ? crypto.randomUUID() : String(Math.random())), description, qty: count, unit, rate, markup: 0 };
      });
      await api.put(`/projects/${projectId}/estimate`, { line_items: [...existing, ...additions] });
      toast.success(`Added ${additions.length} counted item${additions.length === 1 ? "" : "s"} to the estimate${priced ? ` · ${priced} priced from your rate card` : " · set rates in Estimate"}`);
    } catch { toast.error("Couldn't add to estimate"); }
  };

  const onSymbolDown = (e) => {
    if (isPhone || (tool !== "symbol" && tool !== "detect-region")) return;
    const p = point(e); setDragStart(p); setDragBox({ x: p.x, y: p.y, w: 0, h: 0 });
  };
  const onSymbolMove = (e) => {
    if (!dragStart || (tool !== "symbol" && tool !== "detect-region")) return;
    const p = point(e);
    setDragBox({ x: Math.min(dragStart.x, p.x), y: Math.min(dragStart.y, p.y), w: Math.abs(p.x - dragStart.x), h: Math.abs(p.y - dragStart.y) });
  };
  const onSymbolUp = () => {
    if (!dragStart || (tool !== "symbol" && tool !== "detect-region")) return;
    const box = dragBox; setDragStart(null);
    if (tool === "detect-region") {
      setDragBox(null);
      if (!box || box.w < 12 || box.h < 12) { toast.info("Draw a larger box around the area to scan."); setTool("line"); return; }
      detectMeasurements(box);
      return;
    }
    if (!box || box.w < 8 || box.h < 8) { setDragBox(null); toast.info("Draw a box around a single symbol to count its repeats."); return; }
    countSymbols(box);
  };

  const aiDetectScale = async () => {    if (!bg) { toast.error("Open a blueprint page first"); return; }
    setAiBusy("scale");
    try {
      const image = await toAIImage(bg);
      const { data } = await api.post(`/projects/${projectId}/ai/detect-scale`, { image_base64: image });
      if (!data.scale) { toast.info("No scale found on this page — calibrate manually"); return; }
      const pct = Math.round((data.confidence || 0) * 100);
      toast.success(`Detected scale ${data.scale} (${pct}% confident)`, {
        description: "Use Calibrate and click two points on a known dimension to set the exact scale.",
        duration: 8000,
      });
    } catch (err) { toast.error(err.response?.data?.detail || "AI scale detection failed"); }
    finally { setAiBusy(null); }
  };

  const counts = annotations.filter((a) => a.type === "count").length;
  const totalLen = annotations.filter((a) => a.type === "line").reduce((s, a) => s + (a.length || 0), 0);
  const totalArea = annotations.filter((a) => a.type === "area").reduce((s, a) => s + (a.area || 0), 0);
  const areaCount = annotations.filter((a) => a.type === "area").length;
  const effectiveWallTypes = (() => {
    const map = new Map();
    AREA_MATERIALS.forEach((m) => map.set(m.label, { label: m.label, height: m.height, rate: 0 }));
    wallTypes.forEach((w) => map.set(w.label, { label: w.label, height: String(w.height), rate: w.rate }));
    return Array.from(map.values());
  })();
  const symbolLegend = Object.values(annotations.filter((a) => a.type === "count" && a.symbol).reduce((acc, a) => {
    if (!acc[a.symbol]) acc[a.symbol] = { name: a.symbol, color: a.color || "#2563EB", count: 0 };
    acc[a.symbol].count++; return acc;
  }, {}));
  const unit = pageScale?.unit || "px";
  const pageAnns = annotations.filter((a) => (a.page || 1) === pageNum);

  const tools = [
    { key: "count", label: "Count", icon: MousePointerClick },
    { key: "line", label: "Trace / Measure", icon: Ruler },
    { key: "area", label: "Wall / Area", icon: Square },
    { key: "calibrate", label: "Calibrate scale", icon: Crosshair },
    { key: "symbol", label: "Auto-count symbol", icon: Copy },
  ];

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-4">
      {isPhone ? (
        <div className="space-y-3">
          <div className="flex items-start gap-2 text-sm rounded-lg border px-3 py-2.5 bg-amber-50 border-amber-200 text-amber-800" data-testid="takeoff-mobile-note">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <p><span className="font-semibold">View only on mobile.</span> You can see the blueprint and any saved marks here. Tracing, counting and calibrating work best on a tablet or computer, so open this project on a larger screen to edit.</p>
          </div>
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4" data-testid="snap-photo-card">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-600/10 flex items-center justify-center shrink-0">
                <Camera className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="font-display font-semibold text-slate-900 text-sm">Snap a photo, get quantities</p>
                <p className="text-sm text-slate-600 mt-0.5">Photograph a drawing's schedule or legend and let AI pull the quantities straight into your estimate — right here on your phone.</p>
                <input ref={photoRef} type="file" accept="image/*" capture="environment" onChange={onSnapPhoto} className="hidden" data-testid="snap-photo-input" />
                <Button size="sm" onClick={() => photoRef.current?.click()} disabled={snapping} data-testid="snap-photo-btn" className="mt-3 bg-blue-600 hover:bg-blue-700 text-white">
                  {snapping ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Camera className="h-4 w-4 mr-1.5" />} {snapping ? "Reading photo…" : "Snap a photo"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex flex-wrap items-center gap-2">
          {tools.map((t) => (
            <Button key={t.key} size="sm" variant={tool === t.key ? "default" : "outline"} onClick={() => { setTool(t.key); setPending(null); }}
              data-testid={`takeoff-tool-${t.key}`} className={`${isTouch ? "h-11 px-4" : ""} ${tool === t.key ? "bg-blue-600 hover:bg-blue-700" : "border-slate-300"}`}>
              <t.icon className="h-4 w-4 mr-1.5" /> {t.label}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={upload} className="hidden" data-testid="blueprint-file-input" />
          {hasBlueprint && (
            <>
              <Button size="sm" onClick={runAiTakeoff} disabled={!!aiBusy} data-testid="ai-run-takeoff-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
                {aiBusy === "run" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Wand2 className="h-4 w-4 mr-1.5" />} Run AI takeoff
              </Button>
              <Button size="sm" variant="outline" onClick={aiExtract} disabled={!!aiBusy} data-testid="ai-extract-btn" className="border-blue-300 text-blue-700 hover:bg-blue-50">
                {aiBusy === "extract" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} AI quantities
              </Button>
              <Button size="sm" variant="ghost" onClick={() => window.dispatchEvent(new Event("tradeoff:start-tour"))} data-testid="quick-start-btn" className="text-slate-500 hover:text-blue-700">
                <Sparkles className="h-4 w-4 mr-1.5" /> Quick start
              </Button>
              <Button size="sm" variant="outline" onClick={aiDetectScale} disabled={!!aiBusy} data-testid="ai-scale-btn" className="border-blue-300 text-blue-700 hover:bg-blue-50">
                {aiBusy === "scale" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Wand2 className="h-4 w-4 mr-1.5" />} Detect scale
              </Button>
              {isPdf && (
                <Button size="sm" variant="outline" onClick={startDetectRegion} disabled={!!aiBusy} data-testid="detect-measurements-btn" className={`border-emerald-300 text-emerald-700 hover:bg-emerald-50 ${tool === "detect-region" ? "ring-2 ring-emerald-400 bg-emerald-50" : ""}`}>
                  {aiBusy === "detect" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ScanLine className="h-4 w-4 mr-1.5" />} {tool === "detect-region" ? "Draw a box…" : "Detect measurements"}
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={detectAllSymbols} disabled={!!aiBusy} data-testid="detect-all-symbols-btn" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                {aiBusy === "detectall" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Layers className="h-4 w-4 mr-1.5" />} Detect all symbols
              </Button>
              {symbolPresets.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline" disabled={!!aiBusy} data-testid="saved-symbols-btn" className="border-slate-300 text-slate-700 hover:bg-slate-50">
                      <Bookmark className="h-4 w-4 mr-1.5" /> Saved symbols ({symbolPresets.length})
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-60" data-testid="saved-symbols-menu">
                    <DropdownMenuLabel>Re-count a saved symbol</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {symbolPresets.map((p) => (
                      <DropdownMenuItem key={p.id} onClick={() => countFromPreset(p)} data-testid={`saved-symbol-${p.name}`} className="flex items-center gap-2">
                        <span className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: p.color || "#2563EB" }} />
                        <span className="flex-1 truncate">{p.name}</span>
                        <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-600" onClick={(e) => { e.stopPropagation(); deletePreset(p); }} data-testid={`delete-symbol-${p.name}`} />
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {areaCount > 0 && (
                <Button size="sm" variant="outline" onClick={addAreasToEstimate} data-testid="add-areas-estimate-btn" className="border-violet-300 text-violet-700 hover:bg-violet-50">
                  <LayoutGrid className="h-4 w-4 mr-1.5" /> Add areas to estimate
                </Button>
              )}
            </>
          )}
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="upload-blueprint-btn" className="border-slate-300">
            <Upload className="h-4 w-4 mr-1.5" /> {hasBlueprint ? "Replace" : "Upload"} blueprint
          </Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="save-takeoff-btn" className="bg-slate-900 hover:bg-slate-800">
            <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
      )}

      {/* Contextual guidance per tool */}
      {!isPhone && (
      <div className="flex items-start gap-2 text-sm rounded-lg border px-3 py-2.5 bg-blue-50/60 border-blue-100 text-slate-600" data-testid="takeoff-guidance">
        <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
        {tool === "count" && <p><span className="font-semibold text-slate-800">Count:</span> click anywhere on the drawing to drop a numbered marker. Click a marker again to remove it.</p>}
        {tool === "line" && <p><span className="font-semibold text-slate-800">Trace / Measure:</span> click a start point, then an end point to draw a line. If you've calibrated the scale, its real length is shown. <span className="text-slate-500">Right-click a line to edit its length or delete it.</span></p>}
        {tool === "area" && <p><span className="font-semibold text-slate-800">Wall / Area:</span> click the start and end of a wall to measure its length, then enter an optional <span className="font-medium">height</span> — TradeOff calculates the area (length × height) in {unit}². <span className="text-slate-500">Right-click an area line to delete it. Use “Add areas to estimate” to push them as m² lines.</span></p>}
        {tool === "calibrate" && <p><span className="font-semibold text-slate-800">Calibrate scale:</span> pick something on the drawing whose real size you already know — a dimensioned wall, a door, or the scale bar. Click its two ends, then type the real length. Every measurement after that converts to real-world units. {pageScale ? <span className="text-green-700 font-medium">Page {pageNum} is calibrated.</span> : <span className="text-amber-600 font-medium">Page {pageNum} isn't calibrated yet.</span>}</p>}
        {tool === "symbol" && <p><span className="font-semibold text-slate-800">Auto-count symbol:</span> drag a box tightly around one repeated symbol — a door, socket or light. TradeOff finds every matching symbol on this page and drops count markers for them. Review the total before adding.</p>}
        {tool === "detect-region" && <p><span className="font-semibold text-slate-800">Detect in a region:</span> drag a box around the part of the drawing you want to scan — e.g. one room. TradeOff reads only the dimensions inside your box, skipping borders, grid lines and title blocks. You can also filter by line length in the next step.</p>}
      </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-lg p-3"><p className="text-xs text-slate-400">Count total (all pages)</p><p className="font-mono text-xl font-bold text-navy" data-testid="takeoff-count-total">{counts}</p></div>
        <div className="bg-white border border-slate-200 rounded-lg p-3"><p className="text-xs text-slate-400">Length total (all pages)</p><p className="font-mono text-xl font-bold text-navy" data-testid="takeoff-length-total">{totalLen.toFixed(2)}</p></div>
        <div className="bg-white border border-slate-200 rounded-lg p-3"><p className="text-xs text-slate-400">Area total ({unit}²)</p><p className="font-mono text-xl font-bold text-violet-700" data-testid="takeoff-area-total">{totalArea.toFixed(2)}</p></div>
        <div className="bg-white border border-slate-200 rounded-lg p-3"><p className="text-xs text-slate-400">Page {pageNum} scale</p><p className="font-mono text-sm font-semibold text-slate-700" data-testid="takeoff-page-scale">{pageScale ? `1px = ${pageScale.units_per_px.toFixed(4)} ${pageScale.unit}` : "Not calibrated"}</p></div>
      </div>

      {hasBlueprint && !isPhone && symbolLegend.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="symbol-legend">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide mr-1">Counted symbols</span>
          {symbolLegend.map((g) => (
            <span key={g.name} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs" data-testid={`symbol-legend-item-${g.name}`}>
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: g.color }} />
              <span className="font-medium text-slate-700">{g.name}</span>
              <span className="font-mono text-slate-500">×{g.count}</span>
            </span>
          ))}
          <Button size="sm" variant="outline" onClick={addCountsToEstimate} data-testid="add-counts-estimate-btn" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
            <LayoutGrid className="h-4 w-4 mr-1.5" /> Add counts to estimate
          </Button>
        </div>
      )}

      {hasBlueprint && numPages > 1 && (
        <div className="flex items-center justify-center gap-3" data-testid="page-nav">
          <Button size="sm" variant="outline" onClick={() => goPage(pageNum - 1)} disabled={pageNum <= 1} data-testid="page-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm font-medium text-slate-600" data-testid="page-indicator">Page {pageNum} of {numPages}</span>
          <Button size="sm" variant="outline" onClick={() => goPage(pageNum + 1)} disabled={pageNum >= numPages} data-testid="page-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      )}

      {showPanHint && (
        <div className="flex items-center justify-center" data-testid="pan-hint-wrap">
          <div className="inline-flex items-center gap-2 text-xs font-medium text-white bg-slate-900/90 rounded-full px-3.5 py-2 shadow-lg" data-testid="pan-hint">
            <Hand className="h-3.5 w-3.5" /> Pinch to zoom · two fingers to pan
          </div>
        </div>
      )}

      {hasBlueprint && !isPhone && (
        <div className="flex items-center justify-end gap-2" data-testid="zoom-controls">
          <ZoomIn className="h-4 w-4 text-slate-400 mr-1" />
          <Button size="sm" variant="outline" onClick={() => setZoom((z) => clampZoom(z - 0.25))} data-testid="zoom-out" className="h-9 w-9 p-0 border-slate-300"><Minus className="h-4 w-4" /></Button>
          <span className="text-sm font-medium text-slate-600 w-12 text-center tabular-nums" data-testid="zoom-level">{Math.round(zoom * 100)}%</span>
          <Button size="sm" variant="outline" onClick={() => setZoom((z) => clampZoom(z + 0.25))} data-testid="zoom-in" className="h-9 w-9 p-0 border-slate-300"><Plus className="h-4 w-4" /></Button>
          <Button size="sm" variant="outline" onClick={() => setZoom(1)} data-testid="zoom-reset" className="h-9 border-slate-300">Reset</Button>
        </div>
      )}

      <div ref={scrollRef} className="border border-slate-200 rounded-lg bg-slate-100 overflow-auto p-4" style={{ maxHeight: "70vh", touchAction: "pan-x pan-y" }}
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
        {!hasBlueprint ? (
          <div className="h-64 flex flex-col items-center justify-center text-center">
            <Upload className="h-10 w-10 text-slate-300" />
            {isPhone ? (
              <p className="mt-3 text-slate-500 text-sm px-4">No blueprint yet. Open this project on a tablet or computer to upload a drawing and start your takeoff.</p>
            ) : (
              <>
                <p className="mt-3 text-slate-500 text-sm">Upload a PDF (multi-page supported) or image blueprint to start your takeoff.</p>
                <Button onClick={() => fileRef.current?.click()} className="mt-4 bg-blue-600 hover:bg-blue-700" data-testid="empty-upload-btn">Upload blueprint</Button>
              </>
            )}
          </div>
        ) : rendering ? (
          <div className="h-64 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
        ) : (
          <div style={{ width: dims.w * zoom, height: dims.h * zoom }} className="mx-auto">
          <div ref={wrapRef} onClick={isPhone ? undefined : handleClick}
            onMouseDown={onSymbolDown} onMouseMove={onSymbolMove} onMouseUp={onSymbolUp} onMouseLeave={onSymbolUp}
            data-testid="takeoff-canvas"
            className={`relative ${isPhone ? "" : (tool === "count" || tool === "symbol" || tool === "detect-region") ? "cursor-crosshair" : "cursor-copy"}`}
            style={{ width: dims.w, height: dims.h, transform: `scale(${zoom})`, transformOrigin: "top left" }}>
            {bg && <img src={bg} alt="blueprint" width={dims.w} height={dims.h} draggable={false} className="block select-none" />}
            <svg className="absolute inset-0" width={dims.w} height={dims.h}>
              {pageAnns.filter((a) => a.type === "line").map((a) => (
                <g key={a.id}>
                  <line x1={a.x1} y1={a.y1} x2={a.x2} y2={a.y2} stroke="#2563EB" strokeWidth="2" style={{ pointerEvents: "none" }} />
                  <text x={(a.x1 + a.x2) / 2} y={(a.y1 + a.y2) / 2 - 5} fill="#1E3A5F" fontSize="11" fontWeight="600" className="font-mono" style={{ pointerEvents: "none" }}>{(a.length || 0).toFixed(2)} {unit}</text>
                  {a.source === "ocr" && <text x={(a.x1 + a.x2) / 2} y={(a.y1 + a.y2) / 2 + 9} fill="#B45309" fontSize="9" fontWeight="700" className="font-mono" style={{ pointerEvents: "none" }}>OCR — verify</text>}
                  {/* invisible thick hit-line for right-click */}
                  <line x1={a.x1} y1={a.y1} x2={a.x2} y2={a.y2} stroke="transparent" strokeWidth={isTouch ? 26 : 16}
                    style={{ cursor: "context-menu" }} data-testid={`takeoff-line-${a.id}`}
                    onContextMenu={(e) => openMenu(e, a.id)} onClick={(e) => e.stopPropagation()} />
                </g>
              ))}
              {pageAnns.filter((a) => a.type === "area").map((a) => {
                const dxr = a.x2 - a.x1, dyr = a.y2 - a.y1, L = Math.hypot(dxr, dyr) || 1;
                const hpx = a.heightPx || 0;
                const nx = (-dyr / L) * hpx, ny = (dxr / L) * hpx;
                const poly = `${a.x1},${a.y1} ${a.x2},${a.y2} ${a.x2 + nx},${a.y2 + ny} ${a.x1 + nx},${a.y1 + ny}`;
                return (
                  <g key={a.id}>
                    {hpx > 0 && <polygon points={poly} fill="#7C3AED" fillOpacity="0.12" stroke="#7C3AED" strokeOpacity="0.3" strokeWidth="1" style={{ pointerEvents: "none" }} />}
                    <line x1={a.x1} y1={a.y1} x2={a.x2} y2={a.y2} stroke="#7C3AED" strokeWidth="2" strokeDasharray="6 3" style={{ pointerEvents: "none" }} />
                    <text x={(a.x1 + a.x2) / 2} y={(a.y1 + a.y2) / 2 - 5} fill="#5B21B6" fontSize="11" fontWeight="700" className="font-mono" style={{ pointerEvents: "none" }}>{(a.length || 0).toFixed(2)}×{a.height} = {(a.area || 0).toFixed(2)} {unit}²</text>
                    <line x1={a.x1} y1={a.y1} x2={a.x2} y2={a.y2} stroke="transparent" strokeWidth={isTouch ? 26 : 16}
                      style={{ cursor: "context-menu" }} data-testid={`takeoff-area-${a.id}`}
                      onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); removeWithUndo(a.id, "Area"); }} onClick={(e) => e.stopPropagation()} />
                  </g>
                );
              })}
              {pending && <circle cx={pending.x} cy={pending.y} r={isTouch ? 6 : 4} fill="#2563EB" style={{ pointerEvents: "none" }} />}
            {(tool === "symbol" || tool === "detect-region") && dragBox && (dragBox.w > 0 || dragBox.h > 0) && (
              <rect x={dragBox.x} y={dragBox.y} width={dragBox.w} height={dragBox.h} fill="#10B981" fillOpacity="0.12" stroke="#10B981" strokeWidth="1.5" strokeDasharray="5 3" style={{ pointerEvents: "none" }} data-testid="detect-region-rect" />
            )}
            </svg>
            {(() => {
              const seq = {};
              return pageAnns.filter((a) => a.type === "count").map((a) => {
                const key = a.symbol || "_manual";
                seq[key] = (seq[key] || 0) + 1;
                return (
                  <div key={a.id} onClick={(e) => { e.stopPropagation(); removeWithUndo(a.id, a.symbol || "Marker"); }}
                    className={`absolute -translate-x-1/2 -translate-y-1/2 rounded-full text-white font-bold flex items-center justify-center ring-2 ring-white shadow cursor-pointer hover:opacity-75 transition-opacity ${isTouch ? "h-7 w-7 text-xs" : "h-5 w-5 text-[10px]"}`}
                    style={{ left: a.x, top: a.y, backgroundColor: a.color || "#2563EB" }}
                    title={a.symbol ? `${a.symbol} — click to remove` : "Click to remove"}>{seq[key]}</div>
                );
              });
            })()}
          </div>
          </div>
        )}
      </div>

      {/* Right-click line menu */}
      {menu && (
        <div className="fixed z-50 bg-white border border-slate-200 rounded-md shadow-lg py-1 w-40 text-sm" style={{ left: menu.x, top: menu.y }}
          onClick={(e) => e.stopPropagation()} data-testid="line-context-menu">
          <button onClick={startEdit} data-testid="line-edit-btn" className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 text-slate-700"><Pencil className="h-4 w-4" /> Edit length</button>
          <button onClick={deleteFromMenu} data-testid="line-delete-btn" className="w-full flex items-center gap-2 px-3 py-2 hover:bg-red-50 text-red-600"><Trash2 className="h-4 w-4" /> Delete line</button>
        </div>
      )}

      {/* Calibration dialog */}
      <Dialog open={!!calib} onOpenChange={(o) => !o && setCalib(null)}>
        <DialogContent data-testid="calibrate-dialog">
          <DialogHeader>
            <DialogTitle>Set the drawing scale</DialogTitle>
            <DialogDescription>
              You drew a line over a known dimension. Enter its real-world length so TradeOff can convert every measurement on this page into real units.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label htmlFor="calib-len">Real length of the line you drew</Label>
              <Input id="calib-len" type="number" autoFocus value={calib?.value || ""} onChange={(e) => setCalib({ ...calib, value: e.target.value })}
                placeholder="e.g. 5" data-testid="calibrate-length-input" className="mt-1 font-mono" />
            </div>
            <div className="w-28">
              <Label htmlFor="calib-unit">Unit</Label>
              <select id="calib-unit" value={calib?.unit || "m"} onChange={(e) => setCalib({ ...calib, unit: e.target.value })}
                data-testid="calibrate-unit-select" className="mt-1 h-10 w-full border border-slate-300 rounded-md text-sm px-2">
                {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>
          <p className="text-xs text-slate-400">Tip: the more of the drawing your reference line spans, the more accurate your measurements.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCalib(null)}>Cancel</Button>
            <Button onClick={confirmCalibration} data-testid="calibrate-confirm-btn" className="bg-blue-600 hover:bg-blue-700">Set scale</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detected measurements review dialog */}
      <Dialog open={!!detectResult} onOpenChange={(o) => !o && setDetectResult(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="detect-dialog">
          <DialogHeader>
            <DialogTitle>Detected measurements</DialogTitle>
            <DialogDescription>
              {detectResult?.page_type === "raster"
                ? <>Read from a <span className="font-semibold">scanned drawing</span> using OCR. Review carefully, then apply — this calibrates page {pageNum} and drops the ticked dimensions as measured lines.</>
                : <>Read straight from the PDF's vector data. Review, then apply — this calibrates page {pageNum} and drops the ticked dimensions as measured lines. Always sense-check against the drawing.</>}
            </DialogDescription>
          </DialogHeader>
          {detectResult?.page_type === "raster" && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800" data-testid="detect-ocr-note">
              <ScanLine className="h-4 w-4 shrink-0" /> Read with OCR — please verify each value against the drawing.
            </div>
          )}
          {detectResult?.scale_ratio && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="detect-scale-summary">
              Scale detected: <span className="font-semibold">{detectResult.scale_ratio}</span> — will calibrate page {pageNum}.
            </div>
          )}
          {(detectResult?.dims || []).length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="detect-size-filter">
              <p className="text-xs font-semibold text-slate-600 mb-2">Hide noise by line length (pixels)</p>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <Label className="text-xs text-slate-500">Min</Label>
                  <Input type="number" min={0} value={detectFilter.min} onChange={(e) => setDetectFilter((f) => ({ ...f, min: Number(e.target.value) || 0 }))} className="h-8 w-20" data-testid="detect-min-len" />
                </div>
                <div className="flex items-center gap-1.5">
                  <Label className="text-xs text-slate-500">Max</Label>
                  <Input type="number" min={0} value={detectFilter.max} onChange={(e) => setDetectFilter((f) => ({ ...f, max: Number(e.target.value) || 0 }))} className="h-8 w-20" data-testid="detect-max-len" />
                </div>
                <span className="text-[11px] text-slate-400">Long borders & tiny leader lines get filtered out. Set Max to 0 for no upper limit.</span>
              </div>
            </div>
          )}
          <div className="max-h-72 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-lg">
            {(detectResult?.dims || []).length === 0 && <p className="text-sm text-slate-400 text-center py-4">No dimension values matched to lines {detectResult?.scanned_region ? "in that box" : "on this page"}.</p>}
            {(detectResult?.dims || []).filter(detectDimVisible).length === 0 && (detectResult?.dims || []).length > 0 && <p className="text-sm text-slate-400 text-center py-4">All detections are hidden by the length filter — widen the Min/Max range.</p>}
            {(detectResult?.dims || []).filter(detectDimVisible).map((d) => (
              <label key={d._id} className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer" data-testid={`detect-row-${d._id}`}>
                <Checkbox checked={d.include} onCheckedChange={(v) => setDetectResult((r) => ({ ...r, dims: r.dims.map((x) => (x._id === d._id ? { ...x, include: !!v } : x)) }))} data-testid={`detect-check-${d._id}`} />
                <span className="flex-1 text-sm text-slate-700">{d.text}</span>
                <span className="font-mono text-[11px] text-slate-400">{Math.round(d.px_len || 0)}px</span>
                <span className="font-mono text-sm text-slate-500">{d.value_m} m</span>
                {detectResult?.page_type === "raster" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold uppercase tracking-wide" title="Read with OCR — please verify" data-testid={`detect-ocr-badge-${d._id}`}>OCR</span>}
                {d.confidence < 0.7 && <span className="text-[10px] text-amber-600 font-semibold uppercase">low</span>}
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetectResult(null)} data-testid="detect-cancel">Cancel</Button>
            <Button onClick={applyDetection} data-testid="detect-apply" className="bg-emerald-600 hover:bg-emerald-700">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Symbol auto-count confirmation dialog */}
      <Dialog open={!!symbolResult} onOpenChange={(o) => !o && setSymbolResult(null)}>
        <DialogContent data-testid="symbol-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Copy className="h-5 w-5 text-emerald-600" /> Auto-count symbol</DialogTitle>
            <DialogDescription>
              Name this symbol and fine-tune the match strength — TradeOff drops a colour-coded count marker on every match across page {pageNum}.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="symbol-name">Symbol name</Label>
            <Input id="symbol-name" autoFocus value={symbolResult?.name || ""} list="symbol-name-suggestions"
              onChange={(e) => setSymbolResult((r) => (r ? { ...r, name: e.target.value } : r))}
              placeholder="e.g. Double socket" data-testid="symbol-name-input" className="mt-1" />
            <datalist id="symbol-name-suggestions">
              {[...new Set(annotations.filter((a) => a.symbol).map((a) => a.symbol))].map((n) => <option key={n} value={n} />)}
              {presets.map((p) => <option key={p.id || p.description} value={p.description} />)}
            </datalist>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="symbol-strength">Match strength</Label>
              <span className="text-sm font-mono text-slate-600" data-testid="symbol-threshold-value">{Math.round((symbolResult?.threshold || 0.72) * 100)}%</span>
            </div>
            <Slider id="symbol-strength" min={40} max={98} step={2} className="mt-2"
              value={[Math.round((symbolResult?.threshold || 0.72) * 100)]}
              onValueChange={([v]) => setSymbolResult((r) => (r ? { ...r, threshold: v / 100 } : r))}
              onValueCommit={([v]) => recountSymbols(v / 100)}
              data-testid="symbol-threshold-slider" />
            <p className="mt-1 text-xs text-slate-400">Lower catches more (may over-match); higher only near-identical symbols. Release the slider to re-scan.</p>
          </div>
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-center" data-testid="symbol-count-summary">
            {recounting
              ? <div className="flex items-center justify-center gap-2 text-emerald-700 py-1.5"><Loader2 className="h-5 w-5 animate-spin" /> <span className="text-sm font-medium">Re-scanning…</span></div>
              : <>
                  <p className="font-mono text-3xl font-bold text-emerald-700" data-testid="symbol-count-value">{symbolResult?.count || 0}</p>
                  <p className="text-sm text-emerald-800 mt-1">matching symbol{(symbolResult?.count || 0) === 1 ? "" : "s"} found</p>
                </>}
          </div>
          {symbolResult?.preset && (
            <p className="text-xs text-emerald-700" data-testid="symbol-preset-note">Using your saved symbol “{symbolResult.preset.name}”. Colour stays consistent across projects.</p>
          )}
          {symbolResult?.box && (
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer" data-testid="symbol-save-toggle">
              <Checkbox checked={!!symbolResult?.save} onCheckedChange={(v) => setSymbolResult((r) => (r ? { ...r, save: !!v } : r))} data-testid="symbol-save-check" />
              <Bookmark className="h-4 w-4 text-slate-400" /> Save “{(symbolResult?.name || "").trim() || "this symbol"}” to one-tap re-count on future projects
            </label>
          )}
          {symbolResult?.box && symbolResult?.save && (
            <div className="flex items-center gap-2 pl-6" data-testid="symbol-save-rate-row">
              <Label htmlFor="symbol-save-rate" className="text-sm text-slate-600 whitespace-nowrap">Rate (£, optional)</Label>
              <Input id="symbol-save-rate" type="number" min="0" step="0.01" value={symbolResult?.saveRate ?? ""}
                onChange={(e) => setSymbolResult((r) => (r ? { ...r, saveRate: e.target.value } : r))}
                placeholder="e.g. 12.50" data-testid="symbol-save-rate" className="h-9 w-28" />
              <span className="text-xs text-slate-400">so it lands on the estimate already costed</span>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSymbolResult(null)} data-testid="symbol-cancel">Cancel</Button>
            <Button onClick={applySymbols} disabled={recounting || !symbolResult?.count} data-testid="symbol-apply" className="bg-emerald-600 hover:bg-emerald-700">Add {symbolResult?.count || 0} markers</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Whole-set detect review dialog */}
      <Dialog open={!!detectAllResult} onOpenChange={(o) => !o && setDetectAllResult(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="detect-all-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Layers className="h-5 w-5 text-emerald-600" /> Symbols detected</DialogTitle>
            <DialogDescription>
              We grouped the repeated symbols on page {pageNum}. Name each group, untick any you don't want, then drop colour-coded counts. Best-effort — always sense-check against the drawing.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
            {(detectAllResult?.groups || []).map((g, idx) => (
              <div key={g.id} className="flex items-center gap-3 p-3" data-testid={`detect-all-row-${idx}`}>
                <Checkbox checked={g.include} onCheckedChange={(v) => setDetectAllResult((r) => ({ groups: r.groups.map((x, i) => (i === idx ? { ...x, include: !!v } : x)) }))} data-testid={`detect-all-check-${idx}`} />
                <span className="h-4 w-4 rounded-full shrink-0" style={{ backgroundColor: g.color }} />
                {g.template_base64 && <img src={`data:image/png;base64,${g.template_base64}`} alt="" className="h-9 w-9 object-contain border border-slate-200 rounded bg-white p-0.5" />}
                <Input value={g.name} onChange={(e) => setDetectAllResult((r) => ({ groups: r.groups.map((x, i) => (i === idx ? { ...x, name: e.target.value } : x)) }))} data-testid={`detect-all-name-${idx}`} className="flex-1 h-9" placeholder="Symbol name" />
                <span className="font-mono text-sm text-slate-500 w-10 text-right">×{g.count}</span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetectAllResult(null)} data-testid="detect-all-cancel">Cancel</Button>
            <Button onClick={applyDetectAll} data-testid="detect-all-apply" className="bg-emerald-600 hover:bg-emerald-700">Add counts</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wall / area height dialog */}
      <Dialog open={!!areaPrompt} onOpenChange={(o) => !o && setAreaPrompt(null)}>
        <DialogContent data-testid="area-dialog">
          <DialogHeader>
            <DialogTitle>Wall / area measurement</DialogTitle>
            <DialogDescription>
              Line length: <span className="font-mono font-semibold">{areaPrompt?.length?.toFixed(2)} {areaPrompt?.unit}</span>.{" "}
              {pageScale
                ? "Add the wall height to work out the area (length × height), or save the length on its own."
                : "Set the scale first so lengths and heights come out in real metres."}
            </DialogDescription>
          </DialogHeader>

          {!pageScale ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="area-calibrate-nudge">
              This page isn't calibrated yet, so measurements are still in pixels. Set the scale once and every wall length and height will be in real metres — no pixel guesswork.
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="area-material" className="text-sm">Wall type <span className="text-slate-400 font-normal">(optional — sets a typical height)</span></Label>
                  <button type="button" onClick={() => setWallTypeMgr(true)} data-testid="manage-wall-types-btn" className="text-xs text-violet-600 hover:text-violet-800 font-medium">Manage</button>
                </div>
                <select id="area-material" data-testid="area-material-select" value={areaPrompt?.material || ""}
                  onChange={(e) => {
                    const m = effectiveWallTypes.find((x) => x.label === e.target.value);
                    setAreaPrompt((r) => ({ ...r, material: e.target.value, height: m ? m.height : r.height }));
                  }}
                  className="mt-1 w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm">
                  <option value="">Custom…</option>
                  {effectiveWallTypes.map((m) => <option key={m.label} value={m.label}>{m.label} · {m.height} m</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="area-height" className="text-sm">Wall height (m)</Label>
                <div className="mt-1.5 mb-2 flex flex-wrap gap-1.5">
                  {HEIGHT_CHIPS.map((hc) => (
                    <button key={hc} type="button" onClick={() => setAreaPrompt((r) => ({ ...r, height: hc }))}
                      data-testid={`area-height-chip-${hc}`}
                      className={`px-2.5 h-7 rounded-full text-xs font-semibold border transition-colors ${String(areaPrompt?.height) === hc ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-300 hover:border-violet-400"}`}>
                      {hc} m
                    </button>
                  ))}
                </div>
                <Input id="area-height" type="number" min="0" step="0.1" autoFocus value={areaPrompt?.height || ""}
                  onChange={(e) => setAreaPrompt({ ...areaPrompt, height: e.target.value })}
                  placeholder="e.g. 2.4" data-testid="area-height-input" className="font-mono" />
              </div>
              {parseFloat(areaPrompt?.height) > 0 && (
                <p className="text-sm text-violet-700 font-medium" data-testid="area-preview">
                  Area: {(areaPrompt.length * parseFloat(areaPrompt.height)).toFixed(2)} {areaPrompt.unit}²{areaPrompt.material ? ` · ${areaPrompt.material}` : ""}
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => confirmArea(false)} data-testid="area-skip-btn">Save length only</Button>
            {pageScale
              ? <Button onClick={() => confirmArea(true)} data-testid="area-confirm-btn" className="bg-violet-600 hover:bg-violet-700">Save area</Button>
              : <Button onClick={() => { setAreaPrompt(null); setTool("calibrate"); toast.info("Click the two ends of something you know the real length of to set the scale."); }} data-testid="area-calibrate-btn" className="bg-violet-600 hover:bg-violet-700">Calibrate now</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wall types manager */}
      <Dialog open={wallTypeMgr} onOpenChange={setWallTypeMgr}>
        <DialogContent data-testid="wall-types-dialog">
          <DialogHeader>
            <DialogTitle>Your wall types</DialogTitle>
            <DialogDescription>
              Add your own wall types with a default height and rate. They'll appear in the Wall type list and price areas straight onto the estimate.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
            {wallTypes.length === 0 && (
              <p className="p-3 text-sm text-slate-500">You're using the built-in defaults ({AREA_MATERIALS.map((m) => m.label).join(", ")}). Add your own below to customise heights and rates.</p>
            )}
            {wallTypes.map((wt) => (
              <div key={wt.id} className="flex items-center gap-3 p-2.5 text-sm" data-testid={`wall-type-row-${wt.label}`}>
                <span className="flex-1 font-medium text-slate-700 truncate">{wt.label}</span>
                <span className="font-mono text-slate-500">{wt.height} m</span>
                <span className="font-mono text-slate-500 w-16 text-right">{Number(wt.rate) > 0 ? `£${Number(wt.rate).toFixed(2)}` : "—"}</span>
                <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-600 cursor-pointer shrink-0" onClick={() => deleteWallType(wt)} data-testid={`delete-wall-type-${wt.label}`} />
              </div>
            ))}
          </div>
          <div className="flex items-end gap-2 pt-1">
            <div className="flex-1">
              <Label htmlFor="wt-label" className="text-xs">Name</Label>
              <Input id="wt-label" value={newWT.label} onChange={(e) => setNewWT((n) => ({ ...n, label: e.target.value }))} placeholder="e.g. Insulated cladding" data-testid="wall-type-label-input" className="mt-1 h-9" />
            </div>
            <div className="w-20">
              <Label htmlFor="wt-height" className="text-xs">Height (m)</Label>
              <Input id="wt-height" type="number" min="0" step="0.1" value={newWT.height} onChange={(e) => setNewWT((n) => ({ ...n, height: e.target.value }))} data-testid="wall-type-height-input" className="mt-1 h-9 font-mono" />
            </div>
            <div className="w-24">
              <Label htmlFor="wt-rate" className="text-xs">Rate £/m²</Label>
              <Input id="wt-rate" type="number" min="0" step="0.01" value={newWT.rate} onChange={(e) => setNewWT((n) => ({ ...n, rate: e.target.value }))} placeholder="opt." data-testid="wall-type-rate-input" className="mt-1 h-9 font-mono" />
            </div>
            <Button onClick={addWallType} data-testid="wall-type-add-btn" className="bg-violet-600 hover:bg-violet-700 h-9">Add</Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWallTypeMgr(false)} data-testid="wall-types-done-btn">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit line length dialog */}      <Dialog open={!!editLine} onOpenChange={(o) => !o && setEditLine(null)}>
        <DialogContent data-testid="edit-line-dialog">
          <DialogHeader>
            <DialogTitle>Edit line length</DialogTitle>
            <DialogDescription>Override the measured length for this line ({unit}).</DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="edit-len">Length ({unit})</Label>
            <Input id="edit-len" type="number" autoFocus value={editLine?.value || ""} onChange={(e) => setEditLine({ ...editLine, value: e.target.value })}
              data-testid="edit-line-input" className="mt-1 font-mono" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditLine(null)}>Cancel</Button>
            <Button onClick={confirmEdit} data-testid="edit-line-confirm-btn" className="bg-blue-600 hover:bg-blue-700">Save length</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {hasBlueprint && <p className="text-xs text-slate-400">Counts and lengths aggregate across all pages. Calibrate each page separately. Right-click a measured line to edit or delete it.</p>}

      {/* AI quantity extraction review */}
      <Dialog open={!!extractResult} onOpenChange={(o) => !o && setExtractResult(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="ai-extract-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-blue-600" /> AI-extracted quantities</DialogTitle>
            <DialogDescription>Review what the AI read from this drawing, then add the ones you want to your estimate. Always double-check against the drawing.</DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-lg">
            {extractResult?.items.map((it, idx) => (
              <label key={idx} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50" data-testid={`ai-extract-row-${idx}`}>
                <Checkbox checked={it.include} onCheckedChange={(v) => setExtractResult((r) => ({ items: r.items.map((x, i) => i === idx ? { ...x, include: !!v } : x) }))} data-testid={`ai-extract-check-${idx}`} />
                <span className="flex-1 text-sm text-slate-700">{it.description}</span>
                <span className="font-mono text-sm text-slate-500">{it.quantity} {it.unit}</span>
              </label>
            ))}
          </div>
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setExtractResult(null)} disabled={pricing} data-testid="ai-extract-cancel">Cancel</Button>
            <Button variant="outline" onClick={() => applyExtract(false)} disabled={pricing} data-testid="ai-extract-apply" className="border-blue-300 text-blue-700 hover:bg-blue-50">Add unpriced</Button>
            <Button onClick={() => applyExtract(true)} disabled={pricing} data-testid="ai-extract-apply-priced" className="bg-blue-600 hover:bg-blue-700">
              {pricing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Calculator className="h-4 w-4 mr-1.5" />} {pricing ? "Pricing…" : "Add & price with AI"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AskDrawingChatbot
        projectId={projectId}
        hasBlueprint={hasBlueprint}
        getImages={getAllImages}
        pageCount={numPages}
        onPush={pushChatItemToEstimate}
      />
    </div>
  );
};

export default TakeoffCanvas;

